'use client'

import useProductImageState from '@/hooks/useProductImageState'
import useProductState from '@/hooks/useProductState'
import {
  Accordion,
  Card,
  cn,
  DialogFooter,
} from 'oddle-shop-monorepo.design-system'
import { useState, useEffect } from 'react'
import AddToCart from './AddToCart'
import ProductImage from './ProductImage'
import ProductOption from './ProductOption'
import ProductVariant from './ProductVariant'
import { checkExistNutriGradeTag } from '@/components/icons/NutriGrade'

const ProductDetailsModal = ({
  product,
  category,
}: {
  product: Oms.MenuItem
  category: Oms.MenuCategory
}) => {
  const { showDefaultImage } = useProductImageState()
  const existNutriGradeTag = checkExistNutriGradeTag(product.tags)

  const {
    productState,
    totalQuantity,
    totalPrice,
    calculateOptionDiff,
    selectedVariantId,
    calculateVariantDiff,
  } = useProductState(product)

  const hasVariantChoices = product.variants.length > 1
  const subitemCount = product.subitems.length
  const isSimpleItem = !hasVariantChoices && !subitemCount

  // State to control which ProductOption is open
  const [openOptionId, setOpenOptionId] = useState<string | undefined>(
    product?.subitems?.[0]?.id
  )

  // State to control footer animation and scroll
  const [showFooter, setShowFooter] = useState(false)

  // Trigger footer animation after 2 seconds and enable scrolling
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowFooter(true)
    }, 200)

    return () => clearTimeout(timer)
  }, [])

  return (
    <>
      <div className="mb-[70px] flex-1 overflow-x-hidden md:rounded-t-2xl">
        <div className="bg-container p-0 md:p-4">
          <ProductImage
            productState={productState}
            showDefaultImage={showDefaultImage}
            category={category}
            existNutriGradeTag={existNutriGradeTag}
            tags={product.tags}
          />
        </div>
        {!isSimpleItem && (
          <div className="bg-container flex h-full shrink grow flex-col items-center overflow-visible p-0 lg:h-auto lg:max-h-[calc(90vh-124px)]">
            <Card className="bg-container mt-0 w-full rounded-lg px-2.5 lg:p-5">
              {hasVariantChoices && (
                <ProductVariant
                  variants={productState.variants}
                  id={productState.id}
                  selectedVariantId={selectedVariantId}
                  calculateVariantDiff={calculateVariantDiff}
                  calculateOptionDiff={calculateOptionDiff}
                  hideArrow={false}
                />
              )}
              {subitemCount > 0 && (
                <Accordion
                  type="single"
                  collapsible
                  value={openOptionId}
                  onValueChange={setOpenOptionId}
                >
                  {productState.subitems.map((option) => (
                    <ProductOption
                      key={option.id}
                      option={option}
                      hideArrow={false}
                      calculateOptionDiff={calculateOptionDiff}
                      open={openOptionId === option.id}
                    />
                  ))}
                </Accordion>
              )}
            </Card>
          </div>
        )}
      </div>
      <DialogFooter
        className={cn(
          'shadow-sm-inverted absolute bottom-0 z-[1] flex h-[70px] w-full transform select-none flex-row items-center justify-between gap-0 p-2.5 px-4 transition-all duration-500 ease-in-out md:px-6',
          showFooter
            ? 'translate-y-0 opacity-100'
            : 'translate-y-4 opacity-0 md:translate-y-0 md:opacity-100'
        )}
        data-testid="productDetailsModal.footer"
      >
        <AddToCart
          category={category}
          productState={productState}
          totalQuantity={totalQuantity}
          totalItemPrice={totalPrice}
        />
      </DialogFooter>
    </>
  )
}

export default ProductDetailsModal
